import { MissedAtStopEvent, VehicleAtStopEvent, VehicleHistoryEvent, OffRouteEvent } from './events';
import { PassedPosition } from './passed-position';
import { VehicleEvent } from './vehicle-event';

import moment from 'moment';
import * as _ from 'lodash';
import { Deviation } from '../vehicle/vehicle-deviation';
import { VehiclePunctualityConfig } from '../vehicle/vehicle-punctuality-config';
import { ActivityPoint } from '../vehicle/vehicle-activity-point';
import { Geometry } from '@turf/turf';
import { VehicleActivity } from '../vehicle/vehicle-activity';

import * as turf from '@turf/turf';
import { VehicleActivityType, VehicleActivityTypeHelper } from '../vehicle/vehicle-activity-type';
import { VEHICLE_ICON } from '../vehicle/vehicle-icons-enum';


export enum BlockActivityInfoUpdatedBy {
    scrubbarTimelineClick,
    cardClick,
    scroll,
    mapCoordinateClick,
    else
}

export class BlockActivityInfo {
    public trafficDate: Date;
    public from: Date;
    public to: Date;
    public startTime: string;
    public endTime: string;
    public routePublicName?: string;
    public tripNumber?: string;
    public tripDescription?: string;

    public type?: VehicleActivityType;
    public activityType?: string;
    public activityTypeColor?: string;

    public updatedBy = BlockActivityInfoUpdatedBy.else;

    public get time() {
        return this.startTime && this.endTime ? `${this.startTime} - ${this.endTime}` : '';
    }

    public get id() {
        return `${this.tripNumber}:${this.routePublicName}`;
    }

    public get selectorId() {
        return `r${this.routePublicName}t${this.tripNumber}`;
    }

    public events: VehicleHistoryEvent[];
    public positionTimestamps: string[];

    public activityPoints?: ActivityPoint[];
    public shapeGeometry?: Geometry;

    constructor(obj = null) {
        if (obj) {
            Object.assign(this, obj);
        }
    }

    public static fromActivity(activity: VehicleActivity) {
        const result = new BlockActivityInfo({
            trafficDate: activity.trafficDate,
            routePublicName: activity.routePublicName,
            tripNumber: activity.tripNumber,
            activityPoints: activity.activityPoints,
            shapeGeometry: activity.shapeGeometry,
            tripDescription: activity.tripDescription,
            startTime: activity.startTime,
            endTime: activity.endTime,
            type: activity.type,
            events: [],
            positionTimestamps: []
        });

        result.activityTypeColor = VehicleActivityTypeHelper.getColor(result?.type);
        result.activityType = VehicleActivityTypeHelper.getString(result?.type);

        return result;
    }

    public static create(tripNumber: string, routePublicName: string) {
        const result = new BlockActivityInfo({
            routePublicName,
            type: VehicleActivityType.TRIPTRIP,
            tripNumber,
            events: [],
            positionTimestamps: []
        });

        result.activityTypeColor = VehicleActivityTypeHelper.getColor(result?.type);
        result.activityType = VehicleActivityTypeHelper.getString(result?.type);

        return result;
    }

    public toShapeFeature(additionalProperties?: any) {
        if (!this.shapeGeometry) {
            return undefined;
        }

        const properties = {
            zIndex: 10,
            type: 'shape'
        };

        if (additionalProperties) {
            Object.assign(properties, additionalProperties);
        }

        const feature = turf.feature(this.shapeGeometry, properties);
        return feature;
    }
};

// Own information card to use
export class StopInfo extends VehicleHistoryEvent {
    public type = 'StopEventInfo';
    public override get title() {
        return this.name;
    }

    public missed?: boolean;

    public name: string;
    public platformCode: string;
    public sequenceNumber: number;

    public plannedArrival: Date;
    public plannedDeparture: Date;
    public isTimingPoint: boolean;

    public tripNumber: string;
    public routePublicName: string;

    public observedArrival?: Date;
    public observedDeparture?: Date;

    public observedArrivalTs?: string;
    public observedDepartureTs?: string;

    public arrivalDeviation?: Deviation;
    public departureDeviation?: Deviation;

    // To be able to show deviation-colors
    public deviation?: {
        arrival?: {
            color?: string;
            text?: string;
            icon?: string;
        };
        departure?: {
            color?: string;
            text?: string;
            icon?: string;
        };
    };

    // Valid dates
    public from: Date;
    public to: Date;

    public events: VehicleHistoryEvent[];

    public static fromActivity(plannedActivity: ActivityPoint, tripNumber: string, routePublicName: string): StopInfo {
        const stopInfo = new StopInfo();
        stopInfo.isTimingPoint = plannedActivity.isTimingPoint;
        stopInfo.name = plannedActivity.stopDescription;
        stopInfo.sequenceNumber = plannedActivity.sequenceNumber;
        stopInfo.positionTimestamps = [];
        stopInfo.tripNumber = tripNumber;
        stopInfo.routePublicName = routePublicName;
        stopInfo.platformCode = plannedActivity.platformCode;
        stopInfo.events = [];
        return stopInfo;
    }

    public static fromStopEvent(stop: VehicleAtStopEvent | MissedAtStopEvent,
        tripNumber: string, routePublicName: string): StopInfo {
        const stopInfo = new StopInfo();
        stopInfo.isTimingPoint = stop.isTimingPoint;
        stopInfo.name = stop['geoFenceName'];
        stopInfo.plannedArrival = stop.plannedArrival;
        stopInfo.plannedDeparture = stop.plannedDeparture;
        stopInfo.sequenceNumber = stop.sequenceNumber;
        stopInfo.ts = stop.ts;
        stopInfo.positionTimestamps = [];
        stopInfo.tripNumber = tripNumber;
        stopInfo.routePublicName = routePublicName;
        stopInfo.events = [];
        return stopInfo;
    }
}

export class VehicleHistory {
    public inventoryNumber: string;

    public get passedPositions(): PassedPosition[] {
        return this.passedPositionsMap ? [...this.passedPositionsMap.values()] : [];
    }

    // Create these from data, all activites during the time
    public blockActivityInfo: BlockActivityInfo[];
    public passedPositionsMap = new Map<string, PassedPosition>();

    constructor(
        _inventoryNumber: string,
        _passedPositions: PassedPosition[]) {

        this.inventoryNumber = _inventoryNumber;
        this.passedPositionsMap = new Map<string, PassedPosition>();
        _.each(_passedPositions, (p) => {
            this.passedPositionsMap.set(p.id, p);
        });
    }
}

export class VehicleHistoryMapper {
    private static _punctualityConfig: VehiclePunctualityConfig;

    static createVehicleHistory(
        _inventoryNumber: string,
        _passedPositions: PassedPosition[],
        _events: VehicleEvent[],
        _activities: VehicleActivity[],
        _config: VehiclePunctualityConfig
    ): VehicleHistory {
        this._punctualityConfig = _config;

        // Filter out activities to be of TRIPTRIP
        _activities = _.filter(_activities || [], (a: VehicleActivity) => a.type === VehicleActivityType.TRIPTRIP);

        if (_activities?.length === 0) {
            _activities = undefined;
        }

        _passedPositions = this._markOffRoutePositions(_passedPositions, _events)


        const history = new VehicleHistory(
            _inventoryNumber, _passedPositions
        );

        // Map each event to the nearest position
        _.each(_events, ev => {
            const foundPos = history.passedPositionsMap.get(ev.tsId);
            if (foundPos) {
                ev.positionTimestamp = foundPos.ts;
                ev.data.positionTimestamps = [foundPos.id];
            }
        });

        // separate stop events from other events
        const stopEventTypes = ['VehicleAtStopEvent', 'MissedAtStopEvent'];
        const atStopEvents = _.chain(_events)
            .filter((ev: VehicleEvent) => _.includes(stopEventTypes, ev.data.type))
            .map((ev: VehicleEvent) => ev.data)
            .map(ev => ev as VehicleAtStopEvent | MissedAtStopEvent)
            .filter((ev: VehicleAtStopEvent | MissedAtStopEvent) => ev.tripNumber !== '' && ev.routePublicName !== '')
            .sortBy(ev => ev.ts)
            .value();

        const otherEvents: VehicleHistoryEvent[] = _.chain(_events)
            .filter((ev: VehicleEvent) => !_.includes(stopEventTypes, ev.data.type))
            .map((ev: VehicleEvent) => ev.data)
            .sortBy(ev => ev.ts)
            .value();

        // Create information about the activities
        const blockActivities = _.map(_activities, (activity: VehicleActivity) => BlockActivityInfo.fromActivity(activity));

        // Fallback if no activities were found
        if (blockActivities?.length === 0) {
            // Find all unique trip + route
            const stopEventsByTrips = _.chain(_events)
                .filter((ev: VehicleEvent) => ev.data instanceof VehicleAtStopEvent)
                .map((ev: VehicleEvent) => ev.data)
                .map((ev: VehicleAtStopEvent) => ev.tripNumber + ':' + ev.routePublicName)
                .uniq()
                .value();

            // For each trip, group events
            _.each(stopEventsByTrips, tripEvents => {
                const info = tripEvents.split(':');
                const tripNumber = info[0];
                const routePublicName = info[1];
                blockActivities.push(BlockActivityInfo.create(tripNumber, routePublicName));
            });
        }

        const positions = history.passedPositions;

        _.each(blockActivities, activity => {
            this._addInfoToActivity(activity, atStopEvents, otherEvents);

            // Add icon for positions in this activity
            const stopInfos = _.filter(activity.events, e => e.type === 'StopEventInfo') as StopInfo[];

            // Positions that are at the stop
            _.each(stopInfos, stopInfoEvent => {
                const arrival = stopInfoEvent.observedArrival ?? stopInfoEvent.plannedArrival;
                const departure = stopInfoEvent.observedDeparture ?? stopInfoEvent.plannedDeparture;
                const positionsAtStop = _.filter(positions, p => moment(p.ts).isBetween(arrival, departure, null, '[)'));
                _.each(positionsAtStop, pos => {
                    pos.icon = stopInfoEvent.deviation?.arrival?.icon as VEHICLE_ICON;
                    history.passedPositionsMap.set(pos.id, pos);
                });
            });

            // Construct stop-pairs to be able to calculate positions between stops
            const stops: StopInfo[][] = [];
            for (let i = 0; i < stopInfos.length - 1; i++) {
                const pair = [stopInfos[i], stopInfos[i + 1]];
                stops.push(pair);
            }

            _.each(stops, stopPair => {
                const currStop = stopPair[0];
                const nextStop = stopPair[1];

                const departure = currStop.observedDeparture ?? currStop.plannedDeparture;
                const nextArrival = nextStop.observedArrival ?? nextStop.plannedArrival;

                const positionsBetweenStops = _.filter(positions, p => moment(p.ts).isBetween(departure, nextArrival, null, '[)'));

                const departureIcon = currStop.deviation?.departure?.icon as VEHICLE_ICON;
                const arrivalIcon = currStop.deviation?.arrival?.icon as VEHICLE_ICON;

                const icon = departureIcon === VEHICLE_ICON.Default && arrivalIcon !== VEHICLE_ICON.Default ? arrivalIcon : departureIcon;

                // Set icon for all positions in-between the departure and its next arrival
                _.each(positionsBetweenStops, pos => {
                    pos.icon = icon;
                    history.passedPositionsMap.set(pos.id, pos);
                });
            });
        });

        history.blockActivityInfo = blockActivities;
        return history;
    }

    private static _markOffRoutePositions(_passedPositions: PassedPosition[], _events: VehicleEvent[]): PassedPosition[] {
        // Find "offRoute" and "backOnRoute" event pairs
        const offRouteIntervals: { start: Date; end?: Date }[] = [];
        let currentOffRoute: { start: Date; end?: Date } | null = null;

        for (const event of _events) {
            if (event.data instanceof OffRouteEvent) {
                if (event.data.eventType === 'OffRoute') {
                    // Start a new off-route period
                    currentOffRoute = { start: event.ts };
                } else if (event.data.eventType === "BackOnRoute" && currentOffRoute) {
                    // End the off-route period
                    currentOffRoute.end = event.ts;
                    offRouteIntervals.push(currentOffRoute);
                    currentOffRoute = null;
                }
            } else if (currentOffRoute != undefined) {
                currentOffRoute.end = event.ts;
                offRouteIntervals.push(currentOffRoute);
                currentOffRoute = null;
            }
        }

        // If there's an open offRoute interval without a closing "backOnRoute" event, add it
        if (currentOffRoute) {
            offRouteIntervals.push(currentOffRoute);
        }

        // Mark PassedPositions as off-route if they fall within an off-route interval
        for (const position of _passedPositions) {
            position.isOffRoute = offRouteIntervals.some(interval =>
                position.ts >= interval.start && (!interval.end || position.ts <= interval.end)
            );
        }

        return _passedPositions;
    }

    private static _addInfoToActivity(
        activity: BlockActivityInfo,
        atStopEvents: (VehicleAtStopEvent | MissedAtStopEvent)[],
        vehicleEvents: VehicleHistoryEvent[]) {
        const eventsInTrip = [];

        const stopEventsByActivity = _.filter(atStopEvents, (ev) => ev.tripNumber === activity.tripNumber &&
            ev.routePublicName === activity.routePublicName);

        const filteredVehicleEvents = _
            .filter(vehicleEvents, (ev) => {
                if (ev.type === 'OffRouteEvent') {
                    return (<OffRouteEvent>ev).tripNumber === activity.tripNumber
                }
                if (ev.type === 'DoorHistory') return;
                return ev;
            });

        const firstStopEvent = _.first(stopEventsByActivity);
        const lastStopEvent = _.last(stopEventsByActivity);
        const firstOfAllOtherEvents = _.first(filteredVehicleEvents);
        const lastOfAllOtherEvents = _.last(filteredVehicleEvents);

        let firstOfAllEvents: Date;
        if (
            firstStopEvent?.ts 
            && firstOfAllOtherEvents 
            && typeof firstOfAllOtherEvents === 'object' 
            && 'ts' in firstOfAllOtherEvents
        ) {
            firstOfAllEvents = firstStopEvent.ts < firstOfAllOtherEvents.ts 
                ? firstStopEvent.ts 
                : firstOfAllOtherEvents.ts;
        }

        let lastOfAllEvents: Date;
        if (
            lastStopEvent?.ts 
            && lastOfAllOtherEvents 
            && typeof lastOfAllOtherEvents === 'object' 
            && 'ts' in lastOfAllOtherEvents) {
            lastOfAllEvents = lastStopEvent.ts > lastOfAllOtherEvents.ts
                ? lastStopEvent.ts
                : lastOfAllOtherEvents.ts;
        }

        if (activity.trafficDate === undefined) {
            activity.trafficDate = firstStopEvent?.trafficDate;
        }

        activity.from = firstOfAllEvents ?? firstStopEvent?.ts;
        activity.to = lastOfAllEvents ?? lastStopEvent?.ts;

        //  Map certain information from the planned stops
        // eslint-disable-next-line max-len
        const stopInfos = _.map(activity.activityPoints, plannedActivity => StopInfo.fromActivity(plannedActivity, activity.tripNumber, activity.routePublicName));

        // Fallback if no stopinformation is available, then create from events instead
        if (stopInfos?.length === 0) {
            const stopEvents = _.chain(stopEventsByActivity)
                .groupBy((ev) => ev.sequenceNumber)
                .map((value, key) => (_.sortBy(value, 'ts')))
                .value();

            _.each(stopEvents, stopEvent => {
                const stop = _.first(stopEvent);
                const stopInfo = StopInfo.fromStopEvent(stop, stop.tripNumber, stop.routePublicName);
                stopInfos.push(stopInfo);
            });
        }

        // Connect events to each stop
        _.each(stopInfos, stop => {
            const events = _.chain(stopEventsByActivity)
                .filter((ev) => ev.sequenceNumber === stop.sequenceNumber)
                .sortBy((ev) => ev.ts)
                .value();

            // Only save information about the stop if we have any event connected to it
            if (events?.length > 0) {
                const stopCards = VehicleHistoryMapper._constructStopCards(stop, events, vehicleEvents);
                _.each(stopCards, (stopCard: StopInfo) => {
                    stopCard.events = _.sortBy(stopCard.events, 'ts');
                    eventsInTrip.push(stopCard);
                });
            }
        });

        // Try to get events that are between the trip start and end and connect to that
        // eslint-disable-next-line max-len
        const eventsWithinTrip = _.filter(vehicleEvents, (ev: VehicleHistoryEvent) => moment(ev.ts).isBetween(activity.from, activity.to, null, '[]'));
        _.pull(vehicleEvents, ...eventsWithinTrip); // remove the events that where found from the complete list

        const allEvents = eventsInTrip.concat(eventsWithinTrip);
        activity.events = _.sortBy(allEvents, 'ts');
        activity.positionTimestamps = _.chain(activity.events)
            .map((ev: VehicleHistoryEvent) => ev.positionTimestamps)
            .flatten()
            .filter(pos => pos !== undefined)
            .value();
    }

    private static _constructStopCards(
        stopInfo: StopInfo,
        stopEvents: (VehicleAtStopEvent | MissedAtStopEvent)[],
        vehicleEvents: VehicleHistoryEvent[]
    ): StopInfo[] {
        const stopInfos: StopInfo[] = [];

        // Get planned information from the events
        const plannedInfo = _.first(stopEvents);
        stopInfo.plannedArrival = plannedInfo?.plannedArrival;
        stopInfo.plannedDeparture = plannedInfo?.plannedDeparture;

        if (_.findIndex(stopEvents, s => s instanceof MissedAtStopEvent) !== -1) {
            stopInfo.missed = true;
        }

        // Depending on arrival/departure set the observed time
        const vehicleAtStopEvents: VehicleAtStopEvent[] = _.chain(stopEvents)
            .filter(ev => ev instanceof VehicleAtStopEvent)
            .map(ev => ev as VehicleAtStopEvent)
            .sortBy((ev) => ev.ts)
            .value();

        // For displaying values, just take the first and the last event of each type
        const arrivalEvents = _.filter(vehicleAtStopEvents, (ev: VehicleAtStopEvent) => ev.progressType === 'Arrival');
        const departureEvents = _.filter(vehicleAtStopEvents, (ev: VehicleAtStopEvent) => ev.progressType === 'Departure');

        if (arrivalEvents?.length > 1 || departureEvents?.length > 1) {
            // SEVERAL arrival and departure, construct stopCard for each
            let events = [];
            _.each(vehicleAtStopEvents, stopEvent => {
                if (stopEvent.progressType === 'Arrival') {
                    events.push(stopEvent);
                } else if (stopEvent.progressType === 'Departure') {
                    events.push(stopEvent);
                    const card = this._constructStopCard(stopInfo, events, vehicleEvents);
                    stopInfos.push(card);
                    events = [];
                }
            });
        } else {
            const card = this._constructStopCard(stopInfo, vehicleAtStopEvents, vehicleEvents);
            stopInfos.push(card);
        }

        return stopInfos;
    }

    private static _constructStopCard(
        stopInfo: StopInfo,
        vehicleAtStopEvents: VehicleAtStopEvent[],
        vehicleEvents: VehicleHistoryEvent[]
    ) {
        let newStopInfo = _.cloneDeep(stopInfo);
        const arrivalEvent = _.find(vehicleAtStopEvents, (ev: VehicleAtStopEvent) => ev.progressType === 'Arrival');
        const departureEvent = _.findLast(vehicleAtStopEvents, (ev: VehicleAtStopEvent) => ev.progressType === 'Departure');

        newStopInfo.from = arrivalEvent?.ts || departureEvent?.ts;
        newStopInfo.to = departureEvent?.ts || arrivalEvent?.ts;

        // No arrival or departure-event
        if (newStopInfo.from === undefined && newStopInfo.to === undefined) {
            return newStopInfo;
        }

        newStopInfo.ts = newStopInfo.from ?? newStopInfo.ts;

        // The information from MissedAtStopEvent must be wrong since we have actual events
        newStopInfo.missed = false;

        // Calculate observedDeparture and observedArrival
        newStopInfo = this._calculateObservedEvents(newStopInfo, arrivalEvent, departureEvent);

        // Try get all events between the departure and arrival
        if (newStopInfo.observedDeparture && newStopInfo.observedArrival) {
            const eventsBetween = _.filter(vehicleEvents, (ev: VehicleHistoryEvent) =>
                moment(ev.ts).isBetween(newStopInfo.observedArrival, newStopInfo.observedDeparture));

            newStopInfo.positionTimestamps = newStopInfo.positionTimestamps
                .concat(_.flatten(_.map(eventsBetween, e => e.positionTimestamps)));

            // Remove the events that are within the stop-time
            _.pull(vehicleEvents, ...eventsBetween);
            newStopInfo.events = eventsBetween;
        }

        return newStopInfo;
    }

    private static _calculateObservedEvents(stopInfo: StopInfo, arrivalEvent?: VehicleAtStopEvent, departureEvent?: VehicleAtStopEvent) {
        let observedDeparture: moment.Moment; let observedArrival: moment.Moment;

        if (departureEvent) {
            observedDeparture = moment(departureEvent.plannedDeparture).subtract(departureEvent.deviation, 'seconds');
            stopInfo.departureDeviation = new Deviation(departureEvent.deviation);
            if (departureEvent.positionTimestamps) {
                stopInfo.positionTimestamps = stopInfo.positionTimestamps.concat(departureEvent.positionTimestamps);
                stopInfo.observedDepartureTs = departureEvent.positionTimestamps[0];
            }
        }

        if (arrivalEvent) {
            observedArrival = moment(arrivalEvent.plannedArrival).subtract(arrivalEvent.deviation, 'seconds');
            stopInfo.arrivalDeviation = new Deviation(arrivalEvent.deviation);
            if (arrivalEvent.positionTimestamps) {
                stopInfo.positionTimestamps = stopInfo.positionTimestamps.concat(arrivalEvent.positionTimestamps);
                stopInfo.observedArrivalTs = arrivalEvent.positionTimestamps[0];
            }
        }

        // Calculate the punctuality
        // If we want to use the getConfig with the departureEvent then we need the get the departureEvent from the previous stop
        const arrivalValues = this._punctualityConfig.getArrivalConfig(arrivalEvent?.deviation);
        const departureValues = this._punctualityConfig.getConfig(departureEvent?.deviation);

        stopInfo.deviation = {
            arrival: arrivalValues,
            departure: departureValues
        };

        // Calculate the observed times
        stopInfo.observedDeparture = observedDeparture?.toDate();
        stopInfo.observedArrival = observedArrival?.toDate();

        return stopInfo;
    }
}
